import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import monitorApi from "../../api/monitorApi";
import GraphMap from "../../components/GraphMap/GraphMap";
import Header from "../../components/Header/Header";
import MapDescription from "../../components/MapDescription/MapDescription";
import { useSiteData } from "../../SiteDataContext";
import { MapDataType, RegionListType } from "./StatPageTypes";
import SVGGraphColumn from "./SVGGraphColumn/SVGGraphColumn";
import {
  canNavigateToMonthKey,
  getMonthKeyFromDelta,
} from "./SVGGraphColumn/SVGGraphColumnNav/SVGGraphColumnNavUtils";

function StatPage() {
  const { t } = useTranslation();

  const { siteData } = useSiteData();

  const [regionsList, setRegionsList] = useState<RegionListType>([]);

  const [monthDelta, setMonthDelta] = useState(0); // Offset from current month (negative = past)
  const [currentMonth, setCurrentMonth] = useState(0);
  const [yearCounter, setYearCounter] = useState(0);
  const [currentYear, setCurrentYear] = useState(0);
  const [mapData, setMapData] = useState<MapDataType>({});
  const [mapPublishDate, setMapPublishDate] = useState<string | null>(null);
  const [initialCountersSet, setInitialCountersSet] = useState(false);
  const [allStatsData, setAllStatsData] = useState<{
    years_data: Record<number, Array<{ value: number; title: number }>>;
    months_data: Record<string, Array<{ value: number; title: string }>>;
    min_year: number | null;
    max_year: number | null;
    current_year: number;
    current_month: number;
  } | null>(null);

  useEffect(() => {
    const date_now = new Date();
    const warsawDate = new Date(
      date_now.toLocaleString("en-US", { timeZone: "Europe/Warsaw" }),
    );
    setCurrentMonth(warsawDate.getMonth());
    setCurrentYear(warsawDate.getFullYear());
  }, []);

  // Set initial counters to latest available data when allStatsData is loaded
  useEffect(() => {
    if (!allStatsData || !currentYear || initialCountersSet) return;

    // Set year counter to show the latest year with data
    if (allStatsData.max_year !== null) {
      const hasCurrentYearData =
        allStatsData.years_data &&
        allStatsData.years_data[currentYear] &&
        allStatsData.years_data[currentYear].length > 0;

      if (!hasCurrentYearData) {
        // Set counter to show max_year instead of current year
        setYearCounter(allStatsData.max_year - currentYear);
      }
    }

    // Set month delta to show the latest month with data
    if (allStatsData.months_data) {
      const currentMonthKey = `${currentYear}-${(currentMonth + 1).toString().padStart(2, "0")}`;
      const hasCurrentMonthData =
        allStatsData.months_data[currentMonthKey] &&
        allStatsData.months_data[currentMonthKey].length > 0;

      if (!hasCurrentMonthData) {
        // Find the latest month with data
        const monthKeys = Object.keys(allStatsData.months_data).filter(
          (key) =>
            allStatsData.months_data[key] &&
            allStatsData.months_data[key].length > 0,
        );

        if (monthKeys.length > 0) {
          // Sort to find the latest month key (format: "YYYY-MM")
          monthKeys.sort().reverse();
          const latestMonthKey = monthKeys[0];

          // Parse the latest month key to calculate delta
          const [latestYear, latestMonth] = latestMonthKey
            .split("-")
            .map(Number);
          // Calculate delta: difference in months from current date to latest data
          const currentTotalMonths = currentYear * 12 + currentMonth;
          const latestTotalMonths = latestYear * 12 + (latestMonth - 1); // latestMonth is 1-indexed
          const delta = latestTotalMonths - currentTotalMonths;

          setMonthDelta(delta);
        }
      }
    }

    setInitialCountersSet(true);
  }, [allStatsData, currentYear, currentMonth, initialCountersSet]);

  const getMonthKeyFromDeltaForPage = useCallback(
    (delta: number) => getMonthKeyFromDelta(currentYear, currentMonth, delta),
    [currentYear, currentMonth],
  );

  const changeMonthDelta = useCallback(
    (delta: number) => {
      if (!allStatsData?.months_data) return;

      const newDelta = monthDelta + delta;
      const { key: monthKey } = getMonthKeyFromDeltaForPage(newDelta);

      if (
        !canNavigateToMonthKey({
          monthKey,
          months_data: allStatsData.months_data,
          currentYear,
          currentMonth,
        })
      ) {
        return;
      }

      setMonthDelta(newDelta);
    },
    [
      monthDelta,
      allStatsData,
      getMonthKeyFromDeltaForPage,
      currentYear,
      currentMonth,
    ],
  );

  const changeYearCounter = useCallback(
    (delta: number) => {
      if (!allStatsData) return;

      const newYearCounter = yearCounter + delta;
      const targetYear = currentYear + newYearCounter;

      // Check if data exists for this year
      const hasData =
        allStatsData.years_data && allStatsData.years_data[targetYear];

      if (!hasData && delta !== 0) {
        // No data in this direction, don't navigate
        return;
      }

      setYearCounter(newYearCounter);
    },
    [yearCounter, allStatsData, currentYear],
  );

  const make_array_from_i18 = (array1) => {
    return array1.map((array_child) => {
      return array_child.toString();
    });
  };

  const columnsNameArray = useMemo(
    () =>
      make_array_from_i18(
        t("stat.collum_diagram.month", {
          returnObjects: true,
        }),
      ),
    [t],
  );
  useEffect(() => {
    monitorApi
      .getStats()
      .then((response) => {
        const regions_list = response.data.regions_list;
        setRegionsList(regions_list);
      })
      .catch((error) => {
        console.info("There was an error fetching the statistics data!", error);
      });

    monitorApi
      .getLatestShutdownMapReport()
      .then((response) => {
        if (
          response.data.status === "success" &&
          response.data.data.length > 0
        ) {
          const latestReport = response.data.data[0];

          if (latestReport.map_state) {
            setMapData(latestReport.map_state);
          }
          setMapPublishDate(latestReport.map_date);
        }
      })
      .catch((error) => {
        console.info("There was an error fetching the map state!", error);
      });

    // Load all statistics data at once
    monitorApi
      .getAllStats()
      .then((response) => {
        setAllStatsData(response.data);
      })
      .catch((error) => {
        console.info("There was an error fetching all statistics data!", error);
      });
  }, []);

  const to_local_date = (date_utc: string) => {
    let date1 = new Date(Date.parse(date_utc));
    const addDays = 0;
    date1.setUTCDate(date1.getUTCDate() + addDays);

    const day = date1.getUTCDate().toString().padStart(2, "0");
    const month = (date1.getUTCMonth() + 1).toString().padStart(2, "0");
    const year = date1.getUTCFullYear();

    return `${day}.${month}.${year}`;
  };

  return (
    <div className="main-body bg-cream md:pb-10">
      <Header />
      <main className="w-full">
        <section className="w-full justify-between flex">
          <style>
            {`
              .ck-content > ul > li {
                margin-left: 1em;
                font-size: 14px;
              }
              .ck-content > p {
                margin-bottom: 16px;
                font-size: 14px;
                line-height: 120%;
              }
              .text_211b1d {
                color: #211b1d
              }
              .text_8c8c8c {
                color: #8c8c8c
              }

              `}
          </style>
          <div className="container mx-auto">
            <div className="mt-32 mb-8 md:mb-32 px-5 md:px-0 mx-auto max-w-3xl">
              <div className="fw-800 font-bold text-2xl pb-3.5">
                {siteData?.InternetShudowns?.title}
              </div>
              <article>
                <div
                  className="ck-content text_211b1d"
                  dangerouslySetInnerHTML={{
                    __html: siteData?.InternetShudowns?.content,
                  }}
                />
              </article>
            </div>
            <div className="container pl-1-25 bg-cream-light w-full p-2-875 pt-2-5 md:pt-3-3 pb-3 md:plr-8 mb-8 md:mb-32">
              <div className="mx-auto text-center max-w-3xl">
                <div className="fw-800 font-bold text-2xl max-w-2xl mx-auto mb-1">
                  {siteData?.SomethingWrongStat?.title}
                </div>
                <article className="mb-2">
                  <div
                    className="ck-content text_211b1d"
                    dangerouslySetInnerHTML={{
                      __html: siteData?.SomethingWrongStat?.content,
                    }}
                  />
                </article>
              </div>
              <style>{".button1 {background-color:#3C6836}"}</style>
              <div className="flex justify-center">
                <Link
                  to="/choose"
                  className="button1 br-0-5 px-8 py-6 text-base f md:lt-0-02 font-black text-white"
                >
                  {t("hero.report_an_outage")}
                </Link>
              </div>
            </div>
            <div className="w-full flex lg:justify-between lg:flex-row flex-col items-start mb-16 md:mb-32 px-5 md:px-0">
              <div className="max-w-lg mb-8 md:mb-0">
                <div className="fw-800 font-bold text-2xl pb-3.5">
                  {siteData?.ObservingResults?.title}
                </div>
                <article>
                  <div
                    className="ck-content max-w-3xl text_211b1d"
                    dangerouslySetInnerHTML={{
                      __html: siteData?.ObservingResults?.content,
                    }}
                  />
                </article>
              </div>
              <div className="w-full lg:w-auto">
                <SVGGraphColumn
                  data-testid="svg-graph-column-year"
                  display_type={1}
                  title={t("stat.collum_diagram.title")}
                  request_url={null}
                  data={
                    allStatsData && allStatsData.years_data
                      ? allStatsData.years_data[currentYear + yearCounter] || []
                      : null
                  }
                  collums_name_array={columnsNameArray}
                  counter={yearCounter}
                  subtitle={
                    currentYear + yearCounter > 0
                      ? `за ${currentYear + yearCounter} год`
                      : ""
                  }
                  changeCounter={changeYearCounter}
                  legend={t("stat.collum_diagram.data_label")}
                  allStatsData={allStatsData}
                  currentYear={currentYear}
                  currentMonth={currentMonth}
                  dataType="year"
                />
              </div>
            </div>

            <style>{".part5 { max-width: 700px }"}</style>
            <div className="mb-16 md:mb-32 px-5 md:px-0">
              <SVGGraphColumn
                data-testid="svg-graph-column-month"
                display_type={1}
                counter={monthDelta}
                subtitle={(() => {
                  const { month: displayMonth, year: displayYear } =
                    getMonthKeyFromDeltaForPage(monthDelta);
                  return (
                    t("stat.collum_diagram2.subtitle") +
                    t("stat.collum_diagram2.months", { returnObjects: true })[
                      displayMonth
                    ] +
                    ` ${displayYear}`
                  );
                })()}
                title={t("stat.collum_diagram2.title")}
                request_url={null}
                data={
                  allStatsData && allStatsData.months_data
                    ? allStatsData.months_data[
                        getMonthKeyFromDeltaForPage(monthDelta).key
                      ] || []
                    : null
                }
                changeCounter={changeMonthDelta}
                legend={t("stat.collum_diagram2.data_label")}
                allStatsData={allStatsData}
                currentYear={currentYear}
                currentMonth={currentMonth}
                deltaCounter={monthDelta}
                dataType="month"
              />
            </div>
            <style>{".part5 { max-width: 700px }"}</style>
            <div className="part5 mb-8 px-5 md:px-0 mx-auto max-w-3xl">
              <div>
                <div className="fw-800 font-bold text-2xl pb-3.5">
                  {siteData?.PartShutdownDesk?.title}
                </div>
                <article>
                  <div
                    className="ck-content text_211b1d"
                    dangerouslySetInnerHTML={{
                      __html: siteData?.PartShutdownDesk?.content,
                    }}
                  />
                </article>
              </div>
              <div>
                <div className="fw-800 font-bold text-2xl pb-3.5">
                  {siteData?.LocalRegionShutdownDesk?.title}
                </div>
                <article>
                  <div
                    className="ck-content text_211b1d"
                    dangerouslySetInnerHTML={{
                      __html: siteData?.LocalRegionShutdownDesk?.content,
                    }}
                  />
                </article>
              </div>
            </div>

            <div className="container pl-1-25 bg-cream-light mb-32 mt-1-5 w-full p-2-875 pt-2-5 md:pt-3 pb-3 md:plr-3">
              <div className="max-w-md mx-auto mb-2.5">
                <h3 className="text-3xl font-extrabold text-center md:lt-0-08">
                  Карта шатдаунов
                </h3>
                <div className="text_8c8c8c text-lg fw-700 text-center">
                  {t("stat.map.subtitle")}{" "}
                  {mapPublishDate ? to_local_date(mapPublishDate) : ""}
                </div>
              </div>

              {mapData?.shutdowns?.regions ? (
                <>
                  <GraphMap
                    vals={mapData.shutdowns.regions}
                    shutdown_types={mapData.shutdowns.sd_types}
                    regions_list={regionsList}
                  />

                  <MapDescription
                    shutdown_types={mapData.shutdowns.sd_types}
                    display_type={1}
                    placeholder="Недостаточно данных"
                  />
                </>
              ) : (
                ""
              )}
            </div>

            <div className="part5 mb-8 px-5 md:px-0 mx-auto max-w-3xl">
              <div className="mx-auto fw-800 font-bold text-2xl pb-3.5">
                {siteData?.Whiteliststat?.title}
              </div>
              <article>
                <div
                  className="ck-content text_211b1d"
                  dangerouslySetInnerHTML={{
                    __html: siteData?.Whiteliststat?.content,
                  }}
                />
              </article>
            </div>

            <div className="container pl-1-25 bg-cream-light mb-16 mt-1-5 w-full p-2-875 pt-2-5 md:pt-3 pb-3 md:plr-3">
              <div className="max-w-md mx-auto mb-2.5">
                <h3 className="text-3xl font-extrabold text-center md:lt-0-08">
                  Карта белых списков
                </h3>
                <div className="text_8c8c8c text-lg fw-700 text-center">
                  {t("stat.map.subtitle")}{" "}
                  {mapPublishDate ? to_local_date(mapPublishDate) : ""}
                </div>
              </div>

              {mapData?.whitelists?.regions ? (
                <>
                  <GraphMap
                    vals={mapData.whitelists.regions}
                    shutdown_types={mapData.whitelists.sd_types}
                    regions_list={regionsList}
                  />

                  <MapDescription
                    shutdown_types={mapData.whitelists.sd_types}
                    display_type={1}
                    placeholder="Недостаточно данных"
                  />
                </>
              ) : (
                ""
              )}
            </div>

            <div className="container pl-1-25 w-full p-2-875 pt-2-5 md:pt-3-3 pb-3 md:plr-8 mb-8 md:mb-32">
              <div className="mx-auto text-center max-w-3xl">
                <div className="fw-800 font-bold text-2xl pb-3.5 max-w-2xl mx-auto mb-1">
                  {siteData?.SomethingWrongStat?.title}
                </div>
                <article className="mb-2">
                  <div
                    className="ck-content text_211b1d"
                    dangerouslySetInnerHTML={{
                      __html: siteData?.SomethingWrongStat?.content,
                    }}
                  />
                </article>
              </div>
              <style>{".button1 {background-color:#3C6836}"}</style>
              <div className="flex justify-center">
                <Link
                  to="/choose"
                  className="button1 br-0-5 px-8 py-6 text-base f md:lt-0-02 font-black text-white"
                >
                  {t("hero.report_an_outage")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default StatPage;
